import { slotCompareActionTypes } from 'appRedux/actions/slotCompareCommandCreator';
import { IReducer } from './moduleReducerRegistry';
import { ICompareSlotInfo } from 'appRedux/models/casinoModels';
import { OrderByEnum } from 'common/common';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { Insights, resolveCompareInsight, resolveCompareSortBy } from 'config/Insights';

// const defaultSelectedInsight =
//   Insights.find((insight) => insight.defaultCompareSelected)?.value ||
//   '';
const defaultInsight = Insights.find((insight) => insight.defaultCompareSelected) ||
  Insights.find((insight) => insight.value === InsightFilterEnum.ReturnToPlayer) || {
    value: 'none',
  };

export const slotCompareInitialState: ICompareSlotInfo = {
  selectedSlots: [],
  compareSlots: [],
  isComparing: false,
  selectedInsight: defaultInsight.value,
  selectedRange: '',
  filterActive: false,
  settingsActive: false,
  compareSortBy: defaultInsight.value,
  timeFramePrefix: 'm_',
  timeFrameOrderBy: OrderByEnum.Desc,
};

export const slotCompareReducer: IReducer = (draft, action) => {
  const { type, state } = action;
  switch (type) {
    case slotCompareActionTypes.TOGGLE_SELECTED_SLOT:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        const slotIndex = compareSlotsState.selectedSlots.indexOf(state.slotNumber);
        if (slotIndex > -1) {
          compareSlotsState.selectedSlots.splice(slotIndex, 1);
        } else {
          compareSlotsState.selectedSlots.push(state.slotNumber);
        }
      }
      break;
    case slotCompareActionTypes.SET_SLOT_NUMBERS:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.selectedSlots = Array.isArray(state.slotNumbers)
          ? [...state.slotNumbers]
          : [];
      }
      break;
    case slotCompareActionTypes.START_COMPARING:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.compareSlots = [...compareSlotsState.selectedSlots];
        compareSlotsState.isComparing = true;
        compareSlotsState.selectedInsight = resolveCompareInsight(state?.exploreInsight);
        compareSlotsState.compareSortBy = resolveCompareSortBy(state?.exploreInsight);
        compareSlotsState.settingsActive = false;
        // Initialise selected insights: custom default → app default
        const appDefaultInsights = [
          InsightFilterEnum.ReturnToPlayer,
          InsightFilterEnum.Jackpot,
          resolveCompareInsight(state?.exploreInsight),
        ];
        compareSlotsState.selectedInsights =
          Array.isArray(compareSlotsState.customDefaultInsights) &&
          compareSlotsState.customDefaultInsights.length > 0
            ? [...compareSlotsState.customDefaultInsights]
            : appDefaultInsights;
      }
      break;

    case slotCompareActionTypes.STOP_COMPARING:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.isComparing = false;
        compareSlotsState.compareSlots = [];
      }
      break;

    case slotCompareActionTypes.CLEAR_SLOTS:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.selectedSlots = [];
        compareSlotsState.compareSlots = [];
        compareSlotsState.isComparing = false;
        compareSlotsState.selectedInsight = defaultInsight.value;
        compareSlotsState.selectedRange = '';
        compareSlotsState.filterActive = false;
        compareSlotsState.settingsActive = false;
        compareSlotsState.compareSortBy = defaultInsight.value;
        compareSlotsState.timeFramePrefix = 'm_';
        compareSlotsState.timeFrameOrderBy = OrderByEnum.Desc;
      }
      break;

    case slotCompareActionTypes.SET_SELECTED_INSIGHT:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.selectedInsight = state.insight;
        // Close the 3rd-insight picker once an option is chosen.
        compareSlotsState.settingsActive = false;
      }
      break;

    case slotCompareActionTypes.SET_SELECTED_RANGE:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.selectedRange = state.range;
        compareSlotsState.settingsActive = true;
      }
      break;
    case slotCompareActionTypes.SET_COMPARE_SORT_BY:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.compareSortBy = state.compareSortBy;
        compareSlotsState.filterActive = true;
      }
      break;

    case slotCompareActionTypes.SET_TIMEFRAME_PREFIX:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.timeFramePrefix = state.timeframePrefix;
      }
      break;

    case slotCompareActionTypes.SET_TIMEFRAME_ORDER_BY:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.timeFrameOrderBy = state.timeframeOrderBy;
      }
      break;

    case slotCompareActionTypes.SET_TIMEFRAME_AND_ORDER:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.timeFrameOrderBy = state.timeframeOrderBy;
        compareSlotsState.timeFramePrefix = state.timeframePrefix;
      }
      break;

    case slotCompareActionTypes.SET_SETTINGS_ACTIVE:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.settingsActive = state.settingsActive;
      }
      break;

    case slotCompareActionTypes.SET_SELECTED_INSIGHTS:
      if (draft.app?.persistedState?.compareSlotsState) {
        const { compareSlotsState } = draft.app.persistedState;
        compareSlotsState.selectedInsights = state.insights;
        // If current sort column was removed, reset to first selected insight
        if (!state.insights.includes(compareSlotsState.compareSortBy)) {
          compareSlotsState.compareSortBy = state.insights[0] ?? compareSlotsState.compareSortBy;
        }
      }
      break;

    case slotCompareActionTypes.SET_CUSTOM_DEFAULT_INSIGHTS:
      if (draft.app?.persistedState?.compareSlotsState) {
        draft.app.persistedState.compareSlotsState.customDefaultInsights = state.insights;
      }
      break;

    case slotCompareActionTypes.REMOVE_CUSTOM_DEFAULT_INSIGHTS:
      if (draft.app?.persistedState?.compareSlotsState) {
        draft.app.persistedState.compareSlotsState.customDefaultInsights = null;
      }
      break;

    default:
      break;
  }
};

export default slotCompareReducer;
